import axios from 'axios';
import { LoginRequest, RegisterRequest, AuthResponse, ApiResponse } from '@/types';

let API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// During development prefer the Vite proxy (relative '/api') to avoid CORS
try {
  if (typeof window !== 'undefined' && API_BASE_URL) {
    const resolved = new URL(API_BASE_URL, window.location.href);
    if (import.meta.env.MODE === 'development' && resolved.origin !== window.location.origin) {
      API_BASE_URL = '/api';
    }
  }
} catch (e) {
  // ignore URL parse errors and keep the default
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to read tokens from localStorage and treat string 'null'/'undefined' as empty
function readStored(key: string): string | null {
  try {
    const v = localStorage.getItem(key);
    if (!v || v === 'null' || v === 'undefined') return null;
    return v;
  } catch (e) {
    return null;
  }
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = readStored('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Token expired, try to refresh
      const refreshToken = readStored('refreshToken');

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data.data;
          
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          // Retry the original request
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, logout user
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export const authService = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/login', {
      email,
      password,
    });
    
    if (response.data.success) {
      return response.data.data!;
    }
    
    throw new Error(response.data.error?.message || 'Login failed');
  },

  register: async (userData: RegisterRequest): Promise<{ user: any; message: string }> => {
    const response = await api.post<ApiResponse<{ user: any; message: string }>>('/auth/register', userData);
    
    if (response.data.success) {
      return response.data.data!;
    }
    
    throw new Error(response.data.error?.message || 'Registration failed');
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
    }
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  },

  refreshToken: async (refreshToken: string): Promise<{ accessToken: string; refreshToken: string }> => {
    const response = await api.post<ApiResponse<{ accessToken: string; refreshToken: string }>>('/auth/refresh', {
      refreshToken,
    });
    
    if (response.data.success) {
      return response.data.data!;
    }
    
    throw new Error(response.data.error?.message || 'Token refresh failed');
  },

  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await api.post<ApiResponse<{ message: string }>>('/auth/forgot-password', {
      email,
    });
    
    if (response.data.success) {
      return response.data.data!;
    }
    
    throw new Error(response.data.error?.message || 'Failed to send reset email');
  },

  resetPassword: async (token: string, password: string): Promise<{ message: string }> => {
    const response = await api.post<ApiResponse<{ message: string }>>('/auth/reset-password', {
      token,
      password,
    });
    
    if (response.data.success) {
      return response.data.data!;
    }
    
    throw new Error(response.data.error?.message || 'Password reset failed');
  },

  verifyToken: async (token: string): Promise<{ valid: boolean; email?: string }> => {
    const response = await api.post<ApiResponse<{ valid: boolean; email?: string }>>('/auth/verify-token', {
      token,
    });
    
    if (response.data.success) {
      return response.data.data!;
    }
    
    throw new Error(response.data.error?.message || 'Token verification failed');
  },

  getCurrentUser: async (): Promise<{ user: any }> => {
    const response = await api.get<ApiResponse<{ user: any }>>('/user/profile');
    
    if (response.data.success) {
      return response.data.data!;
    }
    
    throw new Error(response.data.error?.message || 'Failed to get current user');
  },
};

export default api;